import { centerOn, followViewport, type Presence, type Rect, type Viewport } from '@kanvas/board-core';
import { useEffect } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { smoothTowards } from '../render/motion';
import { cancelViewportAnimation } from '../state/camera';
import { useEditor } from '../state/store';

/**
 * Follow mode: glue the local camera to another participant.
 *
 * Presence arrives at network rate (cursor ~20 Hz, viewport ~10 Hz). Restarting
 * a short tween on every packet — the old approach — cancelled mid-flight and
 * re-eased from wherever the camera happened to be, which reads as the view
 * "flying" randomly instead of tracking the peer.
 *
 * Instead we keep a sticky target and chase it every frame with exponential
 * smoothing. The focus is their cursor when they have one (so you can watch
 * what they are doing), with zoom matched to the board region they see.
 *
 * Presence is read through a direct subscription rather than a React hook so
 * the board shell does not re-render on every peer pointermove.
 */
export function useFollowPeer(doc: BoardDoc): void {
  const followingUserId = useEditor((s) => s.followingUserId);

  useEffect(() => {
    if (!followingUserId) return;

    cancelViewportAnimation();

    let targetX = useEditor.getState().viewport.x;
    let targetY = useEditor.getState().viewport.y;
    let targetZoom = useEditor.getState().viewport.zoom;
    let frame = 0;
    let lastTick = 0;
    let alive = true;

    const setTarget = (next: Pick<Viewport, 'x' | 'y' | 'zoom'>) => {
      const same =
        Math.abs(next.x - targetX) < 0.05 &&
        Math.abs(next.y - targetY) < 0.05 &&
        Math.abs(next.zoom / targetZoom - 1) < 0.0005;
      if (same) return;
      targetX = next.x;
      targetY = next.y;
      targetZoom = next.zoom;
      if (!frame) {
        lastTick = 0;
        frame = requestAnimationFrame(tick);
      }
    };

    const tick = (now: number) => {
      if (!alive) return;
      const deltaMs = lastTick === 0 ? 16 : Math.min(now - lastTick, 64);
      lastTick = now;

      const vp = useEditor.getState().viewport;
      // Pan chases the pointer tightly; zoom eases a bit slower so a peer's
      // scroll-wheel does not punch the stomach.
      const nextX = smoothTowards(vp.x, targetX, deltaMs, 70);
      const nextY = smoothTowards(vp.y, targetY, deltaMs, 70);
      const nextZoom = Math.exp(
        smoothTowards(Math.log(Math.max(vp.zoom, 1e-6)), Math.log(Math.max(targetZoom, 1e-6)), deltaMs, 110),
      );

      const moved =
        Math.abs(nextX - vp.x) > 0.01 ||
        Math.abs(nextY - vp.y) > 0.01 ||
        Math.abs(nextZoom / vp.zoom - 1) > 0.0005;

      if (moved) {
        useEditor.getState().setViewport((prev) => ({
          ...prev,
          x: nextX,
          y: nextY,
          zoom: nextZoom,
        }));
      }

      const settled =
        Math.abs(nextX - targetX) < 0.05 &&
        Math.abs(nextY - targetY) < 0.05 &&
        Math.abs(nextZoom / targetZoom - 1) < 0.001;

      if (settled) {
        frame = 0;
        lastTick = 0;
        return;
      }
      frame = requestAnimationFrame(tick);
    };

    const apply = () => {
      const peer = doc.getPresence().find((p) => p.userId === followingUserId);
      if (!peer) {
        useEditor.getState().setUi({ followingUserId: null });
        return;
      }

      const next = followCameraTarget(useEditor.getState().viewport, peer);
      if (next) setTarget(next);
    };

    apply();
    const unsub = doc.subscribePresence(apply);
    return () => {
      alive = false;
      unsub();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [doc, followingUserId]);
}

function isValidRemoteView(rect: Rect): boolean {
  return (
    Number.isFinite(rect.x) &&
    Number.isFinite(rect.y) &&
    Number.isFinite(rect.w) &&
    Number.isFinite(rect.h) &&
    rect.w > 1 &&
    rect.h > 1 &&
    rect.w < 1e9 &&
    rect.h < 1e9
  );
}

/**
 * Build the camera we want.
 *
 * Prefer framing the same board region the peer sees — their cursor is then
 * painted inside that frame by `PeerCursorLayer`. Centering on the cursor is
 * only a fallback before they have published a viewport.
 */
export function followCameraTarget(
  local: Viewport,
  peer: Pick<Presence, 'cursor' | 'viewport'>,
): Pick<Viewport, 'x' | 'y' | 'zoom'> | null {
  const remote = peer.viewport && isValidRemoteView(peer.viewport) ? peer.viewport : null;
  if (remote) return followViewport(local, remote);

  const cursor =
    peer.cursor && Number.isFinite(peer.cursor.x) && Number.isFinite(peer.cursor.y)
      ? peer.cursor
      : null;
  if (cursor) return centerOn(local, cursor);

  return null;
}
